import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { H2 } from "~/common/components";
import Select from "~/common/components/ui/select/Select";
import TextArea from "~/common/components/ui/Textarea";
import { generateYearOptions, monthsOptions } from "~/common/constants";
import {
  CompletedIndicatedTakingMedicationsAnswer,
  IndicatedTakingMedicationsAnswer,
  IndicatedTakingMedicationsQuestionData,
} from "~/common/utils/consultations/intake/interfaces";
import { ConsultationQuestionsLayout } from "~/patients/components/consultations/shared";
import { TEXT_MAX_LENGTH } from "~/patients/utils";
import {
  indicatedTakingMedicationsFormSchema,
  IndicatedTakingMedicationsFormValues,
} from "~/patients/utils/consultations/intakes";
import { formatCurrentMedicationCompletedAnswer } from "~/patients/utils/consultations/intakes/formatMedicationCompletedAnswer";
import { formatDateToBE } from "~/providers/utils";

export interface IndicatedTakingMedicationsFormProps {
  question: IndicatedTakingMedicationsQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: IndicatedTakingMedicationsAnswer) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  currentQuestionOptions?: string[];
  completedAnswer?: CompletedIndicatedTakingMedicationsAnswer;
}

const BASE_MED_PATH = "answer.medications_data";

export const IndicatedTakingMedicationsForm = ({
  question,
  onSubmit,
  handleGoBack,
  isSubmitting,
  disableBack,
  currentQuestionOptions,
  completedAnswer,
}: IndicatedTakingMedicationsFormProps) => {
  const { title, subtitle } = question;

  const {
    handleSubmit,
    watch,
    control,
    register,
    formState: { errors },
  } = useForm<IndicatedTakingMedicationsFormValues>({
    resolver: zodResolver(indicatedTakingMedicationsFormSchema),
    defaultValues: {
      answer: {
        medications_data:
          formatCurrentMedicationCompletedAnswer(completedAnswer),
      },
    },
  });

  const saveAnswers: SubmitHandler<IndicatedTakingMedicationsFormValues> = (
    data,
  ) => {
    const medicationsWithMedication = data.answer.medications_data.map(
      (medicationData, index) => ({
        how_did_it_work: medicationData.how_did_it_work,
        date_started: formatDateToBE(
          medicationData.month_started,
          medicationData.year_started,
        ),
        medication: currentQuestionOptions ? currentQuestionOptions[index] : "",
      }),
    );

    onSubmit({ answer: { medications_data: medicationsWithMedication } });
  };

  const medications = watch(BASE_MED_PATH) || [];

  const yearOptions = useMemo(() => generateYearOptions(), []);

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={!medications.length}
      isSubmitting={isSubmitting}
      title={title}
      subtitle={subtitle}
      disableBack={disableBack}
    >
      {currentQuestionOptions?.map((item, index) => (
        <div
          key={item}
          className={twMerge(
            "flex w-full flex-col items-start gap-4 border-neutral-700 pb-8",
            index !== medications.length - 1 && "border-b",
          )}
        >
          <H2>{item}</H2>
          <TextArea
            rows={3}
            id={`${BASE_MED_PATH}.${index}.how_did_it_work`}
            label="How did this medication work for you?"
            placeholder="Please describe (e.g. side effects)."
            {...register(`${BASE_MED_PATH}.${index}.how_did_it_work`)}
            error={
              errors?.answer?.medications_data?.[index]?.how_did_it_work
                ?.message
            }
            charactersCount={
              watch(`${BASE_MED_PATH}.${index}.how_did_it_work`)?.length
            }
            className="border border-gray-20 placeholder:not-italic"
            maxCharacters={TEXT_MAX_LENGTH}
          />
          <div className="flex w-full gap-3">
            <Select
              error={
                errors?.answer?.medications_data?.[index]?.month_started
                  ?.message
              }
              label="Started"
              placeholder={"Select month"}
              id={`${BASE_MED_PATH}.${index}.month_started`}
              options={monthsOptions}
              control={control}
            />
            <div className="flex w-full flex-col items-end justify-end">
              <Select
                error={
                  errors?.answer?.medications_data?.[index]?.month_started
                    ?.message
                }
                label=""
                placeholder={"Select year"}
                id={`${BASE_MED_PATH}.${index}.year_started`}
                options={yearOptions}
                control={control}
              />
            </div>
          </div>
        </div>
      ))}
    </ConsultationQuestionsLayout>
  );
};
